import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Car, ScanLine, ArrowUpRight, Loader2, CircleAlert } from "lucide-react";
import { CRM_API, errMsg } from "./api";
import PageHeader from "./components/PageHeader";

const inp = "w-full bg-white/5 border border-white/12 rounded-lg px-4 py-3 outline-none focus:border-[#1F5EFF] transition-colors text-white text-[15px]";
const Field = ({ label, children, full }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <div className="text-[11px] font-mono-alt text-white/50 mb-1.5 tracking-widest">{label}</div>
    {children}
  </label>
);

export function VehiclesList() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let c = false; setLoading(true);
    const t = setTimeout(() => {
      CRM_API.get(`/vehicles${q ? `?q=${encodeURIComponent(q)}` : ""}`)
        .then(({ data }) => { if (!c) setRows(data); })
        .finally(() => !c && setLoading(false));
    }, 200);
    return () => { c = true; clearTimeout(t); };
  }, [q]);

  return (
    <div data-testid="crm-vehicles-page">
      <PageHeader
        eyebrow="Directory"
        title="Vehicles"
        subtitle="Every vehicle ever brought in — with VIN, plate, mileage and history."
        right={
          <Link to="/crm/vehicles/new" data-testid="btn-new-vehicle"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white font-medium transition-colors">
            <Car className="w-4 h-4" /> New vehicle
          </Link>
        }
      />
      <div className="px-6 md:px-10 py-6">
        <input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by VIN, plate, make, model…"
          className={inp + " mb-6 max-w-md"} data-testid="vehicles-search" />
        {loading ? <div className="text-white/40 text-sm">Loading…</div> :
          rows.length === 0 ? (
            <div className="p-12 text-center border border-dashed border-white/10 rounded-xl">
              <div className="text-white/40 text-sm mb-3">No vehicles yet.</div>
              <Link to="/crm/vehicles/new" className="text-[#1F5EFF] hover:underline">Add first vehicle →</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {rows.map((v) => (
                <Link key={v.id} to={`/crm/vehicles/${v.id}`} data-testid={`vehicle-card-${v.id}`}
                  className="group p-5 rounded-xl bg-white/5 border border-white/10 hover:border-[#1F5EFF]/50 transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-white/10 to-white/2 border border-white/10 flex items-center justify-center">
                      <Car className="w-5 h-5 text-white/70" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium truncate">
                        {[v.year, v.make, v.model].filter(Boolean).join(" ") || "Untitled"}
                      </div>
                      <div className="font-mono-alt text-[10px] text-white/40 truncate">
                        {v.trim || v.color || "—"}
                      </div>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-white/30 group-hover:text-white transition-colors" />
                  </div>
                  <div className="space-y-1 text-xs text-white/50 font-mono-alt">
                    {v.vin && <div>VIN · {v.vin}</div>}
                    {v.license_plate && <div>PLATE · {v.license_plate.toUpperCase()}</div>}
                    {v.customer_name && <div className="text-white/70">{v.customer_name}</div>}
                  </div>
                </Link>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}

export function VehicleNew() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    customer_id: params.get("customer") || "",
    vin: "", license_plate: "", year: "", make: "", model: "", trim: "", color: "", mileage: "", notes: "",
  });
  const [decoding, setDecoding] = useState(false);
  const [decoded, setDecoded] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    CRM_API.get("/customers?limit=200").then(({ data }) => setCustomers(data));
  }, []);

  const decode = async () => {
    const vin = form.vin.trim().toUpperCase();
    if (vin.length < 11) { toast.error("VIN must be at least 11 characters"); return; }
    setDecoding(true); setDecoded(null);
    try {
      const { data } = await CRM_API.post("/vin/decode", { vin });
      setDecoded(data);
      setForm((f) => ({
        ...f,
        vin,
        year: data.year || f.year,
        make: data.make || f.make,
        model: data.model || f.model,
        trim: data.trim || f.trim,
      }));
      if (data.error_code && data.error_code !== "0") {
        toast.warning(`Partial decode: ${data.error_text || "check VIN"}`);
      } else {
        toast.success(`Decoded: ${data.year || "?"} ${data.make || ""} ${data.model || ""}`);
      }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setDecoding(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error("Select a customer first"); return; }
    setBusy(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        vin: form.vin.trim() || null,
        license_plate: form.license_plate.trim() || null,
        year: form.year ? Number(form.year) : null,
        make: form.make.trim() || null,
        model: form.model.trim() || null,
        trim: form.trim.trim() || null,
        color: form.color.trim() || null,
        mileage: form.mileage ? Number(form.mileage) : null,
        notes: form.notes.trim() || null,
      };
      const { data } = await CRM_API.post("/vehicles", payload);
      toast.success("Vehicle saved");
      nav(`/crm/vehicles/${data.id}`, { replace: true });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="crm-vehicle-new-page">
      <PageHeader eyebrow="Directory / new" title="New vehicle" subtitle="Scan the VIN and let us pull year, make, model and trim automatically." />
      <form onSubmit={submit} className="px-6 md:px-10 py-8 max-w-3xl">
        {/* Customer picker */}
        <Field label="Customer *">
          <select required value={form.customer_id} onChange={(e) => set("customer_id", e.target.value)} data-testid="input-customer" className={inp}>
            <option value="">— Select customer —</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.first_name} {c.last_name} · {c.phone}</option>
            ))}
          </select>
        </Field>

        {/* VIN + decoder */}
        <div className="mt-6 p-5 rounded-xl bg-[#1F5EFF]/5 border border-[#1F5EFF]/25">
          <div className="flex items-center gap-2 mb-4">
            <ScanLine className="w-4 h-4 text-[#1F5EFF]" />
            <span className="font-mono-alt text-[10px] tracking-widest text-[#8db2ff] uppercase">VIN Decoder</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input value={form.vin} onChange={(e) => set("vin", e.target.value.toUpperCase())}
              placeholder="Type or paste VIN (17 chars)" maxLength={17}
              className={inp + " font-mono-alt tracking-wider"} data-testid="input-vin" />
            <button type="button" onClick={decode} disabled={decoding || !form.vin}
              data-testid="btn-decode-vin"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white font-medium transition-colors disabled:opacity-50 whitespace-nowrap">
              {decoding ? <Loader2 className="w-4 h-4 animate-spin" /> : "Decode"}
            </button>
          </div>
          {decoded && (
            <div className="mt-3 text-xs text-white/60" data-testid="vin-decode-result">
              {decoded.error_code && decoded.error_code !== "0" && (
                <div className="flex items-center gap-2 text-amber-400 mb-2">
                  <CircleAlert className="w-3.5 h-3.5" /> {decoded.error_text}
                </div>
              )}
              <div className="grid grid-cols-2 gap-1 font-mono-alt uppercase text-[10px]">
                <span>Body: {decoded.body_class || "—"}</span>
                <span>Engine: {decoded.engine_cylinders || "?"} cyl · {decoded.fuel_type || "—"}</span>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Year"><input type="number" value={form.year} onChange={(e) => set("year", e.target.value)} data-testid="input-year" className={inp} inputMode="numeric" /></Field>
          <Field label="Make"><input value={form.make} onChange={(e) => set("make", e.target.value)} data-testid="input-make" className={inp} /></Field>
          <Field label="Model"><input value={form.model} onChange={(e) => set("model", e.target.value)} data-testid="input-model" className={inp} /></Field>
          <Field label="Trim"><input value={form.trim} onChange={(e) => set("trim", e.target.value)} data-testid="input-trim" className={inp} /></Field>
          <Field label="Color"><input value={form.color} onChange={(e) => set("color", e.target.value)} data-testid="input-color" className={inp} /></Field>
          <Field label="License plate"><input value={form.license_plate} onChange={(e) => set("license_plate", e.target.value.toUpperCase())} data-testid="input-plate" className={inp + " uppercase"} /></Field>
          <Field label="Mileage (km)"><input type="number" value={form.mileage} onChange={(e) => set("mileage", e.target.value)} data-testid="input-mileage" className={inp} inputMode="numeric" /></Field>
          <Field label="Notes" full><textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="input-vehicle-notes" rows={3} className={inp + " resize-y"} /></Field>
        </div>
        <div className="mt-8 flex items-center gap-3">
          <button type="submit" disabled={busy} data-testid="btn-save-vehicle"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white font-medium transition-colors disabled:opacity-50">
            {busy ? "Saving…" : "Save vehicle"}
          </button>
          <button type="button" onClick={() => nav(-1)} className="text-white/50 hover:text-white text-sm">Cancel</button>
        </div>
      </form>
    </div>
  );
}

export function VehicleDetail() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const vid = window.location.pathname.split("/").pop();

  useEffect(() => {
    CRM_API.get(`/vehicles/${vid}`).then(({ data }) => setData(data)).catch((e) => setErr(errMsg(e)));
  }, [vid]);

  if (err) return <div className="p-10 text-red-300">{err}</div>;
  if (!data) return <div className="p-10 text-white/40">Loading…</div>;
  const v = data.vehicle;
  return (
    <div data-testid="crm-vehicle-detail">
      <PageHeader
        eyebrow="Vehicle"
        title={[v.year, v.make, v.model].filter(Boolean).join(" ") || "Untitled vehicle"}
        subtitle={v.vin ? `VIN · ${v.vin}` : v.license_plate || ""}
      />
      <div className="px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <aside className="lg:col-span-1 space-y-2 text-sm">
          <InfoRow label="Trim" value={v.trim || "—"} />
          <InfoRow label="Color" value={v.color || "—"} />
          <InfoRow label="Plate" value={v.license_plate || "—"} />
          <InfoRow label="VIN" value={v.vin || "—"} />
          <InfoRow label="Mileage" value={v.mileage ? `${v.mileage.toLocaleString()} km` : "—"} />
          <InfoRow label="Owner" value={data.customer ? `${data.customer.first_name} ${data.customer.last_name || ""}` : "—"} />
          <InfoRow label="Added" value={new Date(v.created_at).toLocaleDateString()} />
        </aside>
        <section className="lg:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-6 h-px bg-[#1F5EFF]" />
            <span className="font-mono-alt text-[10px] tracking-widest text-white/50 uppercase">History</span>
          </div>
          <div className="p-8 text-center border border-dashed border-white/10 rounded-xl text-white/40 text-sm">
            Inspections, work orders and invoices for this vehicle appear here starting Phase 1.2.
          </div>
        </section>
      </div>
    </div>
  );
}

const InfoRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 p-3 rounded-lg bg-white/5 border border-white/8">
    <div className="text-[11px] font-mono-alt text-white/40 uppercase tracking-widest">{label}</div>
    <div className="text-white text-sm text-right break-all">{value}</div>
  </div>
);
